import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchCurrentUser, fetchIdeas, pinIdea, fetchIdeasWithChildren } from '../api/ideas';
import { FaHome, FaPlus, FaUsers, FaBars, FaTimes, FaRegStar, FaStar, FaChevronDown, FaChevronUp, FaChevronRight, FaPalette, FaCog, FaSignOutAlt } from 'react-icons/fa';
import { ThemeContext } from '../context/ThemeContext';

// Recursive component to render idea with children
function IdeaItem({ idea, allIdeas, level = 0, onPin, setIsMobileMenuOpen, token }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const childIdeas = allIdeas.filter(i => i.parent_id === idea.id);
  const hasChildren = childIdeas.length > 0;

  return (
    <li>
      <div 
        className={`group flex items-center justify-between p-2 hover:bg-[var(--bg-hover)] rounded-lg transition-all duration-300 transform hover:scale-102 hover:-translate-y-0.5`}
        style={{ paddingLeft: `${8 + level * 16}px` }}
      >
        {/* Expand/collapse arrow for ideas with children - appears on hover */}
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="mr-1 p-1 hover:bg-[var(--bg-hover)] rounded transition-all duration-200 flex-shrink-0 opacity-0 group-hover:opacity-100 -ml-6 group-hover:ml-0"
            style={{ transition: 'opacity 200ms ease-in-out, margin-left 200ms ease-in-out' }}
          >
            {isExpanded ? (
              <FaChevronDown className="w-3 h-3 text-[var(--text-secondary)]" />
            ) : (
              <FaChevronRight className="w-3 h-3 text-[var(--text-secondary)]" />
            )}
          </button>
        )}
        <Link 
          to={`/idea/${idea.id}`} 
          className="flex-1 flex items-center text-[var(--text-primary)] transition-all duration-200 min-w-0"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <span className="mr-2 flex-shrink-0 transition-transform duration-200 group-hover:scale-110">{idea.icon || '😊'}</span>
          <span className="break-words">{idea.title}</span>
        </Link>
        {/* Only show favorite/pin button for root ideas (no parent) */}
        {!idea.parent_id && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPin(idea.id, idea.is_pinned);
            }}
            className={`ml-1 flex-shrink-0 transition-all duration-200 hover:scale-110 active:scale-95 ${idea.is_pinned ? '' : 'text-[var(--secondary)]'}`}
            style={idea.is_pinned ? { color: 'var(--highlight)' } : {}}
          >
            {idea.is_pinned ? (
              <FaStar className="transition-transform duration-200 hover:rotate-12" style={{ color: 'var(--highlight)' }} />
            ) : (
              <FaRegStar className="transition-transform duration-200 hover:rotate-12" />
            )}
          </button>
        )}
      </div>
      {hasChildren && isExpanded && (
        <ul className="space-y-1">
          {childIdeas.map((child) => (
            <IdeaItem
              key={child.id}
              idea={child}
              allIdeas={allIdeas}
              level={level + 1}
              onPin={onPin}
              setIsMobileMenuOpen={setIsMobileMenuOpen}
              token={token}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function Sidebar({ token, refreshKey, setToken, openSettingsModal }) {
  const [allIdeas, setAllIdeas] = useState([]);
  const [rootIdeas, setRootIdeas] = useState([]);
  const [pinnedIdeas, setPinnedIdeas] = useState([]);
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSubLimitsOpen, setIsSubLimitsOpen] = useState(false);
  const { toggleTheme } = useContext(ThemeContext);
  const navigate = useNavigate();

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('is_admin');
    navigate('/');
  };

  useEffect(() => {
    if (token) {
      fetchUser();
      fetchAllIdeas();
    }
  }, [token, refreshKey]);

  const fetchUser = async () => {
    try {
      const response = await fetchCurrentUser(token);
      setUser(response);
      setError('');
    } catch (err) {
      console.error('Failed to fetch user:', err);
      setError('Failed to fetch user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const fetchAllIdeas = async () => {
    try {
      // Fetch all ideas including children
      const response = await fetchIdeasWithChildren(token);
      setAllIdeas(response);
      
      // Filter root ideas (no parent)
      const roots = response.filter((idea) => !idea.parent_id);
      
      // Separate pinned and unpinned root ideas
      const pinned = roots.filter((idea) => idea.is_pinned);
      const unpinned = roots.filter((idea) => !idea.is_pinned);
      setPinnedIdeas(pinned);
      setRootIdeas(unpinned);
      setError('');
    } catch (err) {
      console.error('Failed to fetch ideas:', err);
      setError('Failed to fetch ideas: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handlePin = async (ideaId, isPinned) => {
    try {
      await pinIdea(ideaId, !isPinned, token);
      await fetchAllIdeas();
    } catch (err) {
      console.error('Pin idea error:', err);
      setError('Failed to pin idea: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Function to render a styled progress bar
  const renderProgressBar = (used, limit, unit = '') => {
    if (limit === null) return null;
    const percentage = Math.min((used / limit) * 100, 100);
    const isNearLimit = percentage > 80;
    const isAtLimit = percentage >= 100;
    
    return (
      <div className="text-sm text-[var(--text-primary)]">
        <div className="w-full bg-[var(--bg-hover)] rounded-full h-2.5 overflow-hidden">
          <div
            className={`h-2.5 rounded-full ${
              isAtLimit 
                ? 'bg-danger' 
                : isNearLimit 
                  ? 'bg-warning' 
                  : 'bg-[var(--text-primary)]'
            }`}
            style={{ 
              width: `${percentage}%`,
              transition: 'width 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
              transformOrigin: 'left center'
            }}
          ></div>
        </div>
        <p className="mt-1 text-xs text-[var(--text-secondary)]">
          {used.toFixed(2)}{unit} / {limit}{unit}
          {isAtLimit && <span style={{ color: 'var(--danger)' }} className="ml-1">(Limit reached)</span>}
          {isNearLimit && !isAtLimit && <span style={{ color: 'var(--warning)' }} className="ml-1">(Near limit)</span>}
        </p>
      </div>
    );
  };

  // Check if all limits are null
  const allLimitsNull = user && (
    user.storage_limit_mb === null &&
    user.ai_message_limit === null &&
    user.creation_ai_limit === null &&
    user.chat_ai_limit === null &&
    user.project_ai_limit === null &&
    user.idea_limit === null
  );

  // Check if sub-limits should be displayed
  const showSubLimits = user && (
    user.enforce_ai_sublimits ||
    user.creation_ai_limit !== null ||
    user.chat_ai_limit !== null ||
    user.project_ai_limit !== null
  );

  return (
    <>
      {/* Mobile Hamburger Button */}
      <button 
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-[var(--bg-secondary)] text-[var(--text-primary)] transition-all duration-200 hover:scale-110 active:scale-95 hover:shadow-md"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        {isMobileMenuOpen ? <FaTimes size={20} className="transition-transform duration-200 hover:rotate-90" /> : <FaBars size={20} className="transition-transform duration-200 hover:rotate-12" />}
      </button>

      {/* Sidebar */}
      <div className={`w-64 bg-[var(--bg-secondary)] p-4 flex flex-col shadow-lg h-full fixed lg:static z-40 transform transition-transform duration-300 ease-in-out lg:m-4 lg:rounded-lg lg:h-[calc(100%-2rem)] ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        {error && (
          <div className="bg-danger-light border text-danger px-3 py-2 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}
        <nav className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto pr-3 -mr-3 scrollbar-thin scrollbar-thumb-[var(--primary)] scrollbar-track-transparent sidebar-scroll">
            <ul className="space-y-2">
              <li>
                <Link
                  to="/"
                  className="flex items-center p-2 text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-all duration-300 transform hover:scale-102 hover:-translate-y-0.5"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <FaHome className="mr-2 transition-transform duration-200 hover:scale-110" />
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/create-idea"
                  className="flex items-center p-2 text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-all duration-300 transform hover:scale-102 hover:-translate-y-0.5"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <FaPlus className="mr-2 transition-transform duration-200 hover:rotate-90" />
                  Create Idea
                </Link>
              </li>
              {user?.is_admin && (
                <li>
                  <Link
                    to="/users"
                    className="flex items-center p-2 text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-all duration-300 transform hover:scale-102 hover:-translate-y-0.5"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <FaUsers className="mr-2 transition-transform duration-200 hover:scale-110" />
                    User Management
                  </Link>
                </li>
              )}
            </ul>
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-2">Pinned Ideas</h3>
              {pinnedIdeas.length > 0 ? (
                <ul className="space-y-1">
                  {pinnedIdeas.map((idea) => (
                    <IdeaItem
                      key={idea.id}
                      idea={idea}
                      allIdeas={allIdeas}
                      level={0}
                      onPin={handlePin}
                      setIsMobileMenuOpen={setIsMobileMenuOpen}
                      token={token}
                    />
                  ))}
                </ul>
              ) : (
                <p className="text-[var(--text-secondary)] text-sm">No pinned ideas</p>
              )}
            </div>
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-2">
                Ideas{user && user.idea_limit !== null ? ` (${user.idea_count}/${user.idea_limit})` : ''}
              </h3>
              {rootIdeas.length > 0 ? (
                <ul className="space-y-1">
                  {rootIdeas.map((idea) => (
                    <IdeaItem
                      key={idea.id}
                      idea={idea}
                      allIdeas={allIdeas}
                      level={0}
                      onPin={handlePin}
                      setIsMobileMenuOpen={setIsMobileMenuOpen}
                      token={token}
                    />
                  ))}
                </ul>
              ) : (
                <p className="text-[var(--text-secondary)] text-sm">No ideas</p>
              )}
            </div>
          </div>
          {/* Usage Limits Section */}
          {user && (
            <div className="mt-4 sticky bottom-0 bg-[var(--bg-secondary)] pt-2">
              <div className="border-t border-[var(--border-color)] pt-2">
                {allLimitsNull ? (
                  <p className="text-sm font-semibold text-[var(--text-primary)] text-center">
                    Unlimited Access
                  </p>
                ) : (
                  <>
                    {/* AI Usage */}
                    {user.ai_message_limit === null && user.creation_ai_limit === null && user.chat_ai_limit === null && user.project_ai_limit === null ? (
                      <p className="text-sm font-semibold text-[var(--text-primary)]">
                        Unlimited AI Usages
                      </p>
                    ) : (
                      <>
                        <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">AI Generations</h3>
                        {user.ai_message_limit !== null && renderProgressBar(user.ai_message_count, user.ai_message_limit)}
                        {showSubLimits && (
                          <div className="mt-2">
                            <button
                              className="flex items-center text-sm font-semibold text-[var(--text-primary)] hover:text-[var(--text-secondary)]"
                              onClick={() => setIsSubLimitsOpen(!isSubLimitsOpen)}
                            >
                              AI Sub-Limits
                              {isSubLimitsOpen ? (
                                <FaChevronUp className="ml-1" />
                              ) : (
                                <FaChevronDown className="ml-1" />
                              )}
                            </button>
                            {isSubLimitsOpen && (
                              <div className="mt-1 pl-2 space-y-1">
                                {user.creation_ai_limit !== null && (
                                  <div className="text-xs text-[var(--text-primary)]">
                                    AI Create: {user.creation_ai_count}/{user.creation_ai_limit}
                                  </div>
                                )}
                                {user.chat_ai_limit !== null && (
                                  <div className="text-xs text-[var(--text-primary)]">
                                    AI Chat: {user.chat_ai_count}/{user.chat_ai_limit}
                                  </div>
                                )}
                                {user.project_ai_limit !== null && (
                                  <div className="text-xs text-[var(--text-primary)]">
                                    AI Project: {user.project_ai_count}/{user.project_ai_limit}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    {/* Storage Usage */}
                    {user.storage_limit_mb === null ? (
                      <p className="text-sm font-semibold text-[var(--text-primary)] mt-2">
                        Unlimited Storage
                      </p>
                    ) : (
                      <>
                        <h3 className="text-sm font-semibold text-[var(--text-primary)] mt-2 mb-1">Storage</h3>
                        {renderProgressBar(user.storage_used_mb, user.storage_limit_mb, 'mb')}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </nav>
      </div>
    </>
  );
}

export default Sidebar;